// src/components/MarketInsights/components/FilterPanel.tsx

import React, { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  Slider,
  TextField,
  Autocomplete,
  Switch,
  Badge,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { Filters, FilterOptions } from "../types";
import { formatCurrency } from "../utils/formatters";

interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  filterOptions: FilterOptions | undefined;
  onFilterChange: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  onReset: () => void;
  activeFilterCount: number;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  onClose,
  filters,
  filterOptions,
  onFilterChange,
  onReset,
  activeFilterCount,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string[]>(["location", "unit", "price"]);

  const handleAccordionChange = (panel: string) => {
    setExpanded((prev) =>
      prev.includes(panel) ? prev.filter((p) => p !== panel) : [...prev, panel]
    );
  };

  const priceRange = filterOptions?.price_range || { min: 0, max: 10000 };
  const sqftRange = filterOptions?.sqft_range || { min: 0, max: 3000 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 380 } },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Badge badgeContent={activeFilterCount} color="primary">
              <FilterIcon />
            </Badge>
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {activeFilterCount > 0 && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={onReset}
                color="inherit"
              >
                Clear All
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Filter Sections */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {/* Location Filters */}
          <Accordion
            expanded={expanded.includes("location")}
            onChange={() => handleAccordionChange("location")}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Location</Typography>
              {filters.neighborhoods.length > 0 && (
                <Chip
                  label={filters.neighborhoods.length}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Autocomplete
                multiple
                options={filterOptions?.neighborhoods || []}
                value={filters.neighborhoods}
                onChange={(_, value) => onFilterChange("neighborhoods", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select neighborhoods"
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />

          {/* Unit Type Filters */}
          <Accordion
            expanded={expanded.includes("unit")}
            onChange={() => handleAccordionChange("unit")}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Unit Type</Typography>
              {filters.bedrooms.length > 0 && (
                <Chip
                  label={filters.bedrooms.length}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Bedrooms
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {(filterOptions?.bedroom_counts || [0, 1, 2, 3, 4]).map((bed) => (
                  <Chip
                    key={bed}
                    label={bed === 0 ? "Studio" : `${bed} BR`}
                    onClick={() => {
                      const newBeds = filters.bedrooms.includes(bed)
                        ? filters.bedrooms.filter((b) => b !== bed)
                        : [...filters.bedrooms, bed];
                      onFilterChange("bedrooms", newBeds);
                    }}
                    color={filters.bedrooms.includes(bed) ? "primary" : "default"}
                    variant={filters.bedrooms.includes(bed) ? "filled" : "outlined"}
                  />
                ))}
              </Box>

            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />

          {/* Price Filters */}
          <Accordion
            expanded={expanded.includes("price")}
            onChange={() => handleAccordionChange("price")}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Price</Typography>
              {filters.priceRange && (
                <Chip
                  label={`${formatCurrency(filters.priceRange[0], {
                    compact: true,
                  })} - ${formatCurrency(filters.priceRange[1], { compact: true })}`}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary">
                Monthly Rent
              </Typography>
              <Box sx={{ px: 1, mt: 2 }}>
                <Slider
                  value={filters.priceRange || [priceRange.min, priceRange.max]}
                  onChange={(_, value) =>
                    onFilterChange("priceRange", value as [number, number])
                  }
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => formatCurrency(v, { compact: true })}
                  min={priceRange.min}
                  max={priceRange.max}
                  step={100}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(priceRange.min)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(priceRange.max)}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 3, display: "block" }}
              >
                Square Footage
              </Typography>
              <Box sx={{ px: 1, mt: 2 }}>
                <Slider
                  value={filters.sqftRange || [sqftRange.min, sqftRange.max]}
                  onChange={(_, value) =>
                    onFilterChange("sqftRange", value as [number, number])
                  }
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v} sf`}
                  min={sqftRange.min}
                  max={sqftRange.max}
                  step={50}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {sqftRange.min} sf
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sqftRange.max} sf
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />

          {/* Amenities — disabled until tagging system is built */}
          <Accordion
            expanded={expanded.includes("amenities")}
            onChange={() => handleAccordionChange("amenities")}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
              opacity: 0.5,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Amenities</Typography>
              <Chip label="Coming soon" size="small" sx={{ ml: 1 }} />
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Amenity filtering will be available once property tagging is set up.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />

          {/* Other Options */}
          <Accordion
            expanded={expanded.includes("other")}
            onChange={() => handleAccordionChange("other")}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Other Options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.petsAllowed === true}
                    onChange={(e) =>
                      onFilterChange(
                        "petsAllowed",
                        e.target.checked ? true : null
                      )
                    }
                  />
                }
                label="Pet Friendly Only"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.availableNowOnly}
                    onChange={(e) =>
                      onFilterChange("availableNowOnly", e.target.checked)
                    }
                  />
                }
                label="Available Now Only"
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={onReset} fullWidth>
            Reset
          </Button>
          <Button variant="contained" onClick={onClose} fullWidth>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterPanel;
